/**
 * Iter-157D — refetch a project payload (carrying the current user's
 * `role` field) whenever permissions might be stale:
 *
 *   • on tab visibility change (user comes back from another tab where
 *     an admin may have promoted / demoted them);
 *   • on window focus (same idea — browsers throttle visibility);
 *   • on the `osf:permissions-stale` CustomEvent dispatched by the
 *     global axios 403 interceptor (catches "you just lost access"
 *     scenarios mid-session);
 *   • on an explicit `refresh()` call returned for action-time refetch.
 *
 * Usage:
 *
 *   const { project, role, loading, error, refresh } =
 *       useProjectRoleRefresh(projectId);
 *
 * The hook owns the fetch. Pages that already manage their own
 * project state should keep their state and call `refresh()` from
 * the listener instead.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";


/**
 * Iter-157E P0(5) — Role-change toast notifier.
 *
 * Iter-158.13.2.16 — scope-explicit copy: every Production-role
 * transition names the scope ("Production Owner", "Production
 * Administrator", …) so users never confuse it with department-scope
 * roles surfaced elsewhere. This notifier fires only for Production
 * role changes (beta_projects.members[].role). Department-scope
 * role changes are surfaced by the dept-member management endpoints
 * with their own explicit "Hair & Makeup" copy.
 */
export function notifyRoleChange(prev, next, projectName) {
    if (!prev || !next || prev === next) return;
    const display = projectName ? ` on ${projectName}` : "";
    if (next === "owner") {
        toast.success(`You are now the Production Owner${display}.`);
        return;
    }
    if (prev === "owner") {
        toast.info(
            `You are no longer the Production Owner${display}. Your Production role is now ${roleLabel(next)}.`,
        );
        return;
    }
    toast.info(`Your Production role${display} changed to ${roleLabel(next)}.`);
}

function roleLabel(role) {
    if (role === "owner") return "Production Owner";
    if (role === "admin") return "Production Administrator";
    if (role === "contributor") return "Production Contributor";
    if (role === "viewer") return "Production Viewer";
    return role || "—";
}


export function useProjectRoleRefresh(projectId, { onRoleChange = null } = {}) {
    const apiBase = useApiBase();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const lastRoleRef = useRef(null);

    const refresh = useCallback(async () => {
        if (!projectId) return;
        try {
            const { data } = await api.get(`${apiBase}/${projectId}`);
            setProject(data);
            const newRole = data?.role || null;
            if (lastRoleRef.current && newRole && lastRoleRef.current !== newRole) {
                onRoleChange?.(lastRoleRef.current, newRole, data);
            }
            lastRoleRef.current = newRole;
            setError("");
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not refresh project.");
        } finally {
            setLoading(false);
        }
    }, [projectId, onRoleChange, apiBase]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "visible") refresh();
        };
        const onFocus = () => refresh();
        const onStale = () => refresh();
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", onFocus);
        window.addEventListener("osf:permissions-stale", onStale);
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("osf:permissions-stale", onStale);
        };
    }, [refresh]);

    return {
        project,
        role: project?.role || null,
        loading,
        error,
        refresh,
    };
}


/**
 * Convenience variant for pages that already manage `project` state
 * via their own fetch — just wire a refetch callback into the global
 * permission-stale events. Returns nothing.
 *
 * Iter-157E P0(5) — pages that already track their own
 * `project.role` can also pass a `currentRole` value; whenever a
 * refetch resolves with a different role (detected by the page
 * itself, via the refetchFn it owns), this hook also exposes a
 * `notifyRoleChange(prev, next)` helper that toasts a calm,
 * literal "You are now the Project Owner." style message. The
 * helper is exported as a named function — `notifyRoleChange` —
 * so any caller can use it directly when they detect a role
 * transition themselves.
 */
export function useRefreshOnPermissionStale(refetchFn) {
    useEffect(() => {
        if (typeof refetchFn !== "function") return undefined;
        const onVis = () => {
            if (document.visibilityState === "visible") refetchFn();
        };
        const onFocus = () => refetchFn();
        const onStale = () => refetchFn();
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", onFocus);
        window.addEventListener("osf:permissions-stale", onStale);
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("osf:permissions-stale", onStale);
        };
    }, [refetchFn]);
}
